import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {
    IndicatorCreateDTO,
    LibraryDomainUseDTO,
    LibraryDTO,
    LibraryGoalUseDTO,
    LibraryIndicatorUseDTO,
    VariableSimpleDTO,
} from "../../../../models/library";
import React from "react";
import {useTranslation} from "react-i18next";
import {API, createApiConfig} from "../../../../utils/API";
import {Box, Button} from "@material-ui/core";
import {
    DeleteActionButton,
    EditActionButton,
    InfoActionButton,
    SwapVerticallyActionButton,
} from "../../../common/buttons";
import {LoadingBackdrop} from "../../../common/Loading";
import {useKeycloak} from "@react-keycloak/web";
import {FormDialog} from "../forms/FormDialog";
import {ConfirmationDialog, InformationDialog} from "../../../common/dialogs";
import {AxiosError} from "axios";
import {valueToUpdate} from "../../../../models/values";
import {IndicatorDialog} from "../../../values/dialogs/IndicatorDialog";
import {IndicatorForm} from "../forms/IndicatorForm";
import ItemPositionInLibrary, {IndicatorPositionInLibrary} from "../util/LibraryItemPosition";
import {HomeIndicator} from "./common/HomeIndicator";
import {ErrorDeleteDTO, rectifyErrorDelete} from "../../../../models/error";
import {LibraryIndicatorRenderDelete} from "./delete/LibraryIndicatorRenderDelete";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        indicatorRow: {
            borderBottom: "1px solid #F9FAFB",
            backgroundColor: "#FAFAFA",
            padding: `${theme.spacing(0.875)}px ${theme.spacing(2)}px ${theme.spacing(0.875)}px ${theme.spacing(7)}px`,
            display: "flex",
            alignItems: "center",
        },
        dragHandle: {
            marginRight: theme.spacing(0.5),
            display: "flex",
            cursor: "move",
            color: "#9CA3AF",
        },
        indicatorItemName: {
            marginLeft: theme.spacing(0.5),
            fontSize: "0.8125rem",
            color: "#6B7280",
            display: "flex",
            alignItems: "center",
            flexGrow: 1,
        },
        infoButton: {},
        ownedIcon: {
            marginLeft: theme.spacing(1),
            fontSize: "80%",
        },
    })
);

interface LibraryIndicatorOwnRowProps {
    library: LibraryDTO;
    libraryDomainUse: LibraryDomainUseDTO;
    libraryGoalUse: LibraryGoalUseDTO;
    libraryIndicatorUse: LibraryIndicatorUseDTO;
    onUpdate: (library: LibraryDTO) => void;
    shiftingItemPosition: ItemPositionInLibrary | null;

    handleShiftStart(itemPosition: IndicatorPositionInLibrary): void;
}

interface LibraryIndicatorOwnRowState {
    name: "info" | "init" | "edit" | "saving" | "delete_confirm" | "delete_inform" | "deleting" | "failed" | "deleted";
    error: ErrorDeleteDTO | null;
    editIndicator: IndicatorCreateDTO;
    usedVariables: VariableSimpleDTO[];
}

export const LibraryIndicatorOwnRow: React.FC<LibraryIndicatorOwnRowProps> = (props: LibraryIndicatorOwnRowProps) => {
    //== Init =================================================================
    const classes = useStyles();
    const [t] = useTranslation("libraries");
    const {keycloak, initialized} = useKeycloak();
    const initState: LibraryIndicatorOwnRowState = {
        name: "init",
        error: null,
        editIndicator: {
            name: props.libraryIndicatorUse.indicator.name,
            tags: props.libraryIndicatorUse.indicator.tags,
            description: props.libraryIndicatorUse.indicator.description,
            definition: props.libraryIndicatorUse.indicator.definition,
            type: props.libraryIndicatorUse.indicator.type,
            direction: props.libraryIndicatorUse.indicator.direction,
            processMethod: props.libraryIndicatorUse.indicator.processMethod,
            presentationRecommendation: props.libraryIndicatorUse.indicator.presentationRecommendation,
            validityComment: props.libraryIndicatorUse.indicator.validityComment,
            formula: props.libraryIndicatorUse.indicator.formula,
            preferredUnitUUID: props.libraryIndicatorUse.indicator.preferredUnit?.id ?? null,
            maximumValueUpdate: valueToUpdate(props.libraryIndicatorUse.indicator.maximumValue),
            minimumValueUpdate: valueToUpdate(props.libraryIndicatorUse.indicator.minimumValue),
            usedVariables: props.libraryIndicatorUse.indicator.usedVariables.map((v) => v.id),
            libraryUUID: props.libraryIndicatorUse.indicator.libraryUUID,
            libraryGoalUseUUID: props.libraryGoalUse.id,
            orderNumber: props.libraryIndicatorUse.orderNumber,
        },
        usedVariables: props.libraryIndicatorUse.indicator.usedVariables,
    };
    const [state, setState] = React.useState(initState);
    //== Effects ==============================================================
    React.useEffect(() => {
        if (state.name === "saving") {
            const url = `/indicators/in-library/${props.libraryIndicatorUse.id}`;
            console.log(state.editIndicator);
            API.put<LibraryDTO>(url, state.editIndicator, createApiConfig(keycloak, initialized))
                .then((res) => {
                    console.log(res);
                    props.onUpdate(res.data);
                    //setState({...state, name: "init"});
                })
                .catch((err: AxiosError) => {
                    console.log(err);
                    setState({...state, name: "failed"});
                });
        } else if (state.name === "deleting") {
            API.delete<LibraryDTO>(
                `/indicators/in-library/${props.libraryIndicatorUse.id}`,
                createApiConfig(keycloak, initialized)
            )
                .then((res) => {
                    props.onUpdate(res.data);
                    //setState({...state, name: "deleted"});
                })
                .catch((err: AxiosError) => {
                    if (err.response?.status === 400) {
                        const error: ErrorDeleteDTO = rectifyErrorDelete(err.response.data);
                        setState({...state, name: "delete_inform", error: error});
                    } else {
                        setState({...state, name: "failed"});
                    }
                });
        }
    }, [state, setState, props]);
    //== Handlers =============================================================
    const handleInfoOpen = () => {
        setState({...state, name: "info"});
    };
    const handleEditOpen = () => {
        setState({...state, name: "edit"});
    };
    const handleEditChange = (indicator: IndicatorCreateDTO, usedVariables: VariableSimpleDTO[]) => {
        setState({...state, editIndicator: indicator, usedVariables: usedVariables, name: "edit"});
    };
    const handleEditSubmit = () => {
        setState({...state, name: "saving"});
    };
    const handleDelete = () => {
        setState({...state, name: "delete_confirm"});
    };
    const handleDeleteConfirm = () => {
        setState({...state, name: "deleting"});
    };
    const handleReset = () => {
        setState({...state, name: "init"});
    };
    const isShiftingOtherThanThisOne =
        props.shiftingItemPosition &&
        (props.shiftingItemPosition.domainIndex !== props.libraryDomainUse.orderNumber ||
            props.shiftingItemPosition.goalIndex !== props.libraryGoalUse.orderNumber ||
            props.shiftingItemPosition.indicatorIndex !== props.libraryIndicatorUse.orderNumber);
    const libraryPublished = props.library.public || props.library.shared;
    //== Render ===============================================================
    return (
        <Box className={classes.indicatorRow}>
            <div className={classes.dragHandle} style={isShiftingOtherThanThisOne ? {visibility: "hidden"} : {}}>
                <SwapVerticallyActionButton
                    onClick={() =>
                        props.handleShiftStart({
                            domainIndex: props.libraryDomainUse.orderNumber,
                            goalIndex: props.libraryGoalUse.orderNumber,
                            indicatorIndex: props.libraryIndicatorUse.orderNumber,
                        })
                    }
                />
            </div>
            <Box className={classes.indicatorItemName}>
                {props.libraryIndicatorUse.indicator.name}
                <HomeIndicator />
            </Box>
            <Box className={classes.infoButton}>
                <InfoActionButton onClick={handleInfoOpen} />
            </Box>
            <Box>
                <EditActionButton onClick={handleEditOpen} />
                {!libraryPublished && <DeleteActionButton onClick={handleDelete} />}
            </Box>
            <IndicatorDialog
                indicator={props.libraryIndicatorUse.indicator}
                handleClose={handleReset}
                open={state.name === "info"}
            />
            <FormDialog
                id={`edit-indicator-${props.libraryIndicatorUse.indicator.id}`}
                title={t("indicator.edit.name")}
                open={state.name === "edit"}
                onClose={handleReset}
                actions={[
                    <Button key="submit" onClick={handleEditSubmit}>
                        {t("indicator.edit.button")}
                    </Button>,
                ]}
            >
                <IndicatorForm
                    indicator={state.editIndicator}
                    usedVariables={state.usedVariables}
                    original={props.libraryIndicatorUse.indicator}
                    published={libraryPublished}
                    optionsHandle="allow"
                    onChange={handleEditChange}
                />
            </FormDialog>
            <ConfirmationDialog
                title={t("indicator.confirm_delete_own.title")}
                text={t("indicator.confirm_delete_own.text", {title: props.libraryIndicatorUse.indicator.name})}
                open={state.name === "delete_confirm"}
                onAgree={handleDeleteConfirm}
                onDisagree={handleReset}
                id={"confirm-delete-dialog-" + props.libraryIndicatorUse.id}
            />
            {state.error !== null && (
                <InformationDialog
                    id={`delete-inform-dialog-${props.libraryIndicatorUse.id}`}
                    title={t(`indicator.confirm_delete.inform.title`)}
                    text={t(`indicator.confirm_delete.inform.text`, {
                        title: props.libraryIndicatorUse.indicator.name ?? "",
                        count: state.error?.relatedEntities.length ?? 0,
                    })}
                    open={state.name === "delete_inform"}
                    onClose={handleReset}
                >
                    <LibraryIndicatorRenderDelete
                        entity={{
                            name: props.libraryIndicatorUse.indicator.name,
                            id: props.libraryIndicatorUse.id,
                        }}
                        error={state.error}
                    />
                </InformationDialog>
            )}
            {state.name.endsWith("ing") && <LoadingBackdrop />}
        </Box>
    );
};
